// Routes and sign-in guards.
//
// REMEMBER: these guards decide what to SHOW and where to SEND people. They are
// not security. A person who types a URL they should not see just gets an empty
// screen, because the database (row-level security) refuses to return the rows.
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router'
import { canOpenDepartment, canWriteDepartment, homeFor, noAccessReason } from '@/auth/profile'
import type { Profile } from '@/auth/profile'
import type { AuthState } from '@/auth/SessionProvider'
import { AppLayout } from '@/layouts/AppLayout'
import { FirstLoginPage } from '@/features/auth/FirstLoginPage'
import { LoginPage } from '@/features/auth/LoginPage'
import { NoAccessPage } from '@/features/auth/NoAccessPage'
import { AccountPage, AdminHome, DeptHome } from '@/features/home/HomePages'
import { MembersPage } from '@/features/members/MembersPage'
import { MemberDetailPage } from '@/features/members/MemberDetailPage'
import { DuesPage } from '@/features/dues/DuesPage'
import { ArrearsPage } from '@/features/dues/ArrearsPage'
import { PayPage } from '@/features/payments/PayPage'
import { PaymentsPage } from '@/features/payments/PaymentsPage'
import { BudgetsPage } from '@/features/budgets/BudgetsPage'
import { BudgetDetailPage } from '@/features/budgets/BudgetDetailPage'
import { ProjectsPage } from '@/features/projects/ProjectsPage'
import { ProjectDetailPage } from '@/features/projects/ProjectDetailPage'
import { SettingsPage } from '@/features/admin/SettingsPage'
import { PeoplePage } from '@/features/admin/PeoplePage'
import { QuarterReportPage } from '@/features/reports/QuarterReportPage'
import { ChurchSummaryPage } from '@/features/reports/ChurchSummaryPage'
import { ReconcilePage } from '@/features/reconcile/ReconcilePage'
import { ApprovalsPage } from '@/features/approvals/ApprovalsPage'
import { RemittancesPage } from '@/features/remittances/RemittancesPage'
import { SheetsPage } from '@/features/sheets/SheetsPage'
import { ImportMembersPage } from '@/features/members/ImportMembersPage'
import { ExpensesPage } from '@/features/expenses/ExpensesPage'
import { IncomePage } from '@/features/income/IncomePage'
import { DeletionsPage } from '@/features/owner/DeletionsPage'
import { HistoryPage } from '@/features/history/HistoryPage'

export interface RouterContext {
  auth: AuthState
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <main className="min-h-dvh grid place-items-center p-6 text-center">
      <div>
        <h1 className="text-title font-semibold text-ink">Page not found</h1>
        <p className="mt-2 text-muted">That page does not exist.</p>
        <a href="/" className="mt-4 inline-block text-brand hover:underline">
          Go to the start
        </a>
      </div>
    </main>
  ),
})

/** Where a signed-in person belongs right now. */
function destination(profile: Profile | null): '/first-login' | '/no-access' | '/' {
  if (noAccessReason(profile)) return '/no-access'
  if (profile?.mustChangePassword) return '/first-login'
  return '/'
}

/** For the public screens: signed-in people are sent on to where they belong. */
function redirectIfSignedIn(auth: AuthState) {
  if (auth.status === 'signed-in') {
    const p = auth.profile
    // A person who has no record or is switched off may not use the app, so
    // they go to the explanation page rather than round in circles.
    if (noAccessReason(p)) throw redirect({ to: '/no-access' })
    throw redirect({ to: p?.mustChangePassword ? '/first-login' : '/' })
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: ({ context }) => redirectIfSignedIn(context.auth),
  component: LoginPage,
})

const firstLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/first-login',
  beforeLoad: ({ context }) => {
    const a = context.auth
    if (a.status !== 'signed-in') throw redirect({ to: '/login' })
    if (destination(a.profile) !== '/first-login') throw redirect({ to: destination(a.profile) })
  },
  component: FirstLoginPage,
})

const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/no-access',
  beforeLoad: ({ context }) => {
    const a = context.auth
    if (a.status !== 'signed-in') throw redirect({ to: '/login' })
    if (destination(a.profile) !== '/no-access') throw redirect({ to: destination(a.profile) })
  },
  component: NoAccessPage,
})

/** Everything inside the signed-in frame requires a fully set-up person. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context }) => {
    const a = context.auth
    if (a.status !== 'signed-in') throw redirect({ to: '/login' })
    const where = destination(a.profile)
    if (where !== '/') throw redirect({ to: where })
    return { profile: a.profile as Profile }
  },
  component: AppLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const home = homeFor(context.profile)
    if (!home) throw redirect({ to: '/no-access' })
    throw redirect(home)
  },
})

const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  beforeLoad: ({ context }) => {
    if (!context.profile.isChurchReader) {
      const home = homeFor(context.profile)
      throw redirect(home ?? { to: '/no-access' })
    }
  },
  component: AdminHome,
})

const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/approvals',
  beforeLoad: ({ context }) => {
    if (!context.profile.isChurchReader) {
      const home = homeFor(context.profile)
      throw redirect(home ?? { to: '/no-access' })
    }
  },
  component: ApprovalsPage,
})

const churchReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/reports',
  beforeLoad: ({ context }) => {
    if (!context.profile.isChurchReader) {
      const home = homeFor(context.profile)
      throw redirect(home ?? { to: '/no-access' })
    }
  },
  component: ChurchSummaryPage,
})

function requireChurchReader({ context }: { context: { profile: Profile } }) {
  if (!context.profile.isChurchReader) {
    const home = homeFor(context.profile)
    throw redirect(home ?? { to: '/no-access' })
  }
}

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/settings',
  beforeLoad: requireChurchReader,
  component: SettingsPage,
})

const peopleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/people',
  beforeLoad: requireChurchReader,
  component: PeoplePage,
})

const incomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/income',
  beforeLoad: requireChurchReader,
  component: IncomePage,
})

const deletionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/deletions',
  beforeLoad: requireChurchReader,
  component: DeletionsPage,
})

const historyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/history',
  beforeLoad: requireChurchReader,
  component: () => <HistoryPage />,
})

const reconcileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/reconcile',
  beforeLoad: ({ context }) => {
    if (!context.profile.isChurchReader) {
      const home = homeFor(context.profile)
      throw redirect(home ?? { to: '/no-access' })
    }
  },
  component: ReconcilePage,
})

/** /d/:deptId  -- anyone who may look at the department; children render in the Outlet. */
const deptRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/d/$deptId',
  beforeLoad: ({ context, params }) => {
    if (!canOpenDepartment(context.profile, params.deptId)) {
      const home = homeFor(context.profile)
      throw redirect(home ?? { to: '/no-access' })
    }
  },
  component: () => <Outlet />,
})

/** Screens that RECORD things: read-only people are sent back to the department page. */
function requireWriter({ context, params }: { context: { profile: Profile }; params: { deptId: string } }) {
  if (!canWriteDepartment(context.profile, params.deptId)) {
    throw redirect({ to: '/d/$deptId', params: { deptId: params.deptId } })
  }
}

const deptIndexRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: '/',
  component: function DeptIndex() {
    const { deptId } = deptIndexRoute.useParams()
    return <DeptHome deptId={deptId} />
  },
})

const membersRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'members',
  component: function Members() {
    const { deptId } = membersRoute.useParams()
    return <MembersPage deptId={deptId} />
  },
})

const importMembersRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'members/import',
  component: function ImportMembers() {
    const { deptId } = importMembersRoute.useParams()
    return <ImportMembersPage deptId={deptId} />
  },
})

const memberRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'members/$memberId',
  component: function Member() {
    const { deptId, memberId } = memberRoute.useParams()
    return <MemberDetailPage deptId={deptId} memberId={memberId} />
  },
})

const payRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'pay',
  validateSearch: (s: Record<string, unknown>): { member?: string } => ({
    member: typeof s.member === 'string' ? s.member : undefined,
  }),
  beforeLoad: requireWriter,
  component: function Pay() {
    const { deptId } = payRoute.useParams()
    const { member } = payRoute.useSearch()
    return <PayPage deptId={deptId} memberId={member} />
  },
})

const paymentsRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'payments',
  component: function Payments() {
    const { deptId } = paymentsRoute.useParams()
    return <PaymentsPage deptId={deptId} />
  },
})

const duesRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'dues',
  component: function Dues() {
    const { deptId } = duesRoute.useParams()
    return <DuesPage deptId={deptId} />
  },
})

const arrearsRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'arrears',
  component: function Arrears() {
    const { deptId } = arrearsRoute.useParams()
    return <ArrearsPage deptId={deptId} />
  },
})

const budgetsRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'budgets',
  component: function Budgets() {
    const { deptId } = budgetsRoute.useParams()
    return <BudgetsPage deptId={deptId} />
  },
})

const budgetRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'budgets/$quarterId',
  component: function Budget() {
    const { deptId, quarterId } = budgetRoute.useParams()
    return <BudgetDetailPage deptId={deptId} quarterId={quarterId} />
  },
})

const expensesRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'expenses',
  component: function Expenses() {
    const { deptId } = expensesRoute.useParams()
    return <ExpensesPage deptId={deptId} />
  },
})

const remittancesRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'remittances',
  component: function Remittances() {
    const { deptId } = remittancesRoute.useParams()
    return <RemittancesPage deptId={deptId} />
  },
})

const sheetsRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'sheets',
  component: function Sheets() {
    const { deptId } = sheetsRoute.useParams()
    return <SheetsPage deptId={deptId} />
  },
})

const deptReportRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'report',
  component: function DeptReport() {
    const { deptId } = deptReportRoute.useParams()
    return <QuarterReportPage deptId={deptId} />
  },
})

const deptHistoryRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'history',
  component: function DeptHistory() {
    const { deptId } = deptHistoryRoute.useParams()
    return <HistoryPage deptId={deptId} />
  },
})

const projectsRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'projects',
  component: function Projects() {
    const { deptId } = projectsRoute.useParams()
    return <ProjectsPage deptId={deptId} />
  },
})

const projectRoute = createRoute({
  getParentRoute: () => deptRoute,
  path: 'projects/$projectId',
  component: function Project() {
    const { deptId, projectId } = projectRoute.useParams()
    return <ProjectDetailPage deptId={deptId} projectId={projectId} />
  },
})

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  firstLoginRoute,
  noAccessRoute,
  appRoute.addChildren([
    indexRoute,
    adminRoute,
    approvalsRoute,
    reconcileRoute,
    incomeRoute,
    deletionsRoute,
    historyRoute,
    settingsRoute,
    peopleRoute,
    churchReportRoute,
    deptRoute.addChildren([
      deptIndexRoute, membersRoute, importMembersRoute, memberRoute, payRoute, paymentsRoute, duesRoute, arrearsRoute, budgetsRoute, budgetRoute, expensesRoute, remittancesRoute, sheetsRoute, deptReportRoute, deptHistoryRoute, projectsRoute, projectRoute,
    ]),
    accountRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  // Real value is supplied by <RouterProvider context=...> in App.tsx.
  context: { auth: { status: 'loading' } },
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
